// src/components/MapaCalor.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebaseConfig';
import { 
    collection, addDoc, getDocs, doc, deleteDoc, updateDoc, query, where, getDoc 
} from 'firebase/firestore';
import { 
    FaStore, FaMapMarkerAlt, FaListUl, FaTrash, FaCheck, 
    FaSyncAlt, FaExclamationTriangle, FaPen, FaRedo, FaSearch, FaRoute, FaTimes, FaPlus 
} from 'react-icons/fa';
import '../styles/Cadastro.css'; // Estilos globais
import '../styles/MapaCalor.css'; // O CSS Responsivo e Bonito
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from 'react-leaflet';
import L from 'leaflet';

// --- CONFIGURAÇÃO LEAFLET (Ícone Padrão) ---
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

// --- UTILS ---
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;
    const R = 6371; // Raio da Terra em km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a = 
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; 
};

// Gerador de Ícones Personalizados (Avatar Redondo)
const createCustomIcon = (imageUrl, type) => {
  const isStore = type.includes('marker-loja');
  const size = isStore ? 55 : 40;
  const borderColor = isStore ? '#002454' : '#ffffff'; 
  const zIndex = isStore ? 1000 : 500; 

  return L.divIcon({
    className: 'custom-div-icon', 
    html: `<div style="
            width: ${size}px; 
            height: ${size}px; 
            background-image: url('${imageUrl}'); 
            background-size: cover; 
            background-position: center; 
            background-color: white;
            border-radius: 50%; 
            border: 3px solid ${borderColor};
            box-shadow: 0 4px 8px rgba(0,0,0,0.3);
            position: relative;
            z-index: ${zIndex};
          "></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -(size / 2)]
  });
};

// Componente para atualizar a visão do mapa
const ChangeView = ({ center, zoom }) => {
  const map = useMap();
  map.setView(center, zoom);
  return null;
};

// --- COMPONENTE DE PAGINAÇÃO ---
const Pagination = ({ currentPage, totalPages, onPageChange, itemsPerPage, onItemsPerPageChange }) => {
    if (totalPages === 0) return null;
  
    const getPageNumbers = () => {
      const delta = 1; 
      const range = [];
      const rangeWithDots = [];
      let l;
      for (let i = 1; i <= totalPages; i++) {
        if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) range.push(i);
      }
      for (let i of range) {
        if (l) {
          if (i - l === 2) rangeWithDots.push(l + 1);
          else if (i - l !== 1) rangeWithDots.push('...');
        }
        rangeWithDots.push(i);
        l = i;
      }
      return rangeWithDots;
    };
  
    return (
      <div className="pagination-container">
          <div style={{display:'flex', alignItems:'center', gap:'10px', fontSize:'0.85rem', color:'#64748b'}}>
              <span>Itens:</span>
              <select className="mc-select" style={{width:'auto', height:'32px', padding:'0 10px'}} value={itemsPerPage} onChange={(e) => onItemsPerPageChange(Number(e.target.value))}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
              </select>
          </div>
          <nav>
            <ul className="pagination-wrapper">
              <li>
                  <button className="page-button" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>&lt;</button>
              </li>
              {getPageNumbers().map((number, index) => (
                  <li key={index}>
                      {number === '...' ? (
                          <span style={{padding:'0 8px'}}>...</span>
                      ) : (
                          <button 
                            className={`page-button ${number === currentPage ? 'active' : ''}`} 
                            onClick={() => onPageChange(number)}
                          >
                              {number}
                          </button>
                      )}
                  </li>
              ))}
              <li>
                  <button className="page-button" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>&gt;</button>
              </li>
            </ul>
          </nav>
      </div>
    );
};

// --- COMPONENTE PRINCIPAL ---
const MapaCalor = ({ userData, visualSettings }) => {
    const [activeTab, setActiveTab] = useState('mapa'); 
    
    // Dados
    const [lojas, setLojas] = useState([]);
    const [usersAnalysis, setUsersAnalysis] = useState([]);
    
    // Filtros
    const [searchTerm, setSearchTerm] = useState('');
    const [filterLoja, setFilterLoja] = useState('');
    const [filterStatus, setFilterStatus] = useState(''); 
    const [filterRadius, setFilterRadius] = useState(0); 
    const [lojaSearchTerm, setLojaSearchTerm] = useState(''); // Filtro Específico para aba Lojas
    
    // Processamento
    const [loading, setLoading] = useState(false);
    const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0, isSyncing: false });
    
    // Paginação
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Mapa
    const [mapCenter, setMapCenter] = useState([-14.2350, -51.9253]); // Centro do Brasil
    const [mapZoom, setMapZoom] = useState(4);

    // Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingLojaId, setEditingLojaId] = useState(null);
    const [novaLoja, setNovaLoja] = useState({ nome: '', endereco: '', numero: '', bairro: '', cidade: '', estado: '', cep: '' });

    // --- EFEITOS ---
    useEffect(() => {
        if (userData?.empresa_id) {
            fetchLojas();
            fetchUsersForAnalysis();
        }
    }, [userData]);

    // Reseta paginação ao filtrar
    useEffect(() => { setCurrentPage(1); }, [searchTerm, filterLoja, filterStatus, itemsPerPage, filterRadius]);

    // --- FETCH DATA ---
    const fetchLojas = async () => {
        const q = collection(db, 'Empresas', userData.empresa_id, 'lojas');
        const snap = await getDocs(q);
        setLojas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    const fetchUsersForAnalysis = async () => {
        setLoading(true);
        try {
            // ATUALIZAÇÃO IMPORTANTE: Filtra por empresa E por status 'active' diretamente no banco
            const qUsers = query(
                collection(db, 'users'), 
                where("empresa_id", "==", userData.empresa_id),
                where("status", "==", "active") // Garante que apenas ativos venham
            );
            
            const usersSnap = await getDocs(qUsers);
            const storesSnap = await getDocs(collection(db, 'Empresas', userData.empresa_id, 'lojas'));
            const currentStores = storesSnap.docs.map(d => ({id: d.id, ...d.data()}));
            const analysisData = [];

            for (const userDoc of usersSnap.docs) {
                const userPublic = userDoc.data();
                
                // Dupla checagem de segurança (caso o filtro do banco falhe por indexação)
                if (userPublic.status !== 'active') continue;

                const privSnap = await getDoc(doc(db, 'usersprivado', userDoc.id));
                const privData = privSnap.exists() ? privSnap.data() : {};
                
                const userLojaNome = (userPublic.loja || '').trim().toLowerCase();
                const lojaDestino = currentStores.find(l => l.nome.trim().toLowerCase() === userLojaNome);
                const userPhoto = userPublic.photoURL || 'https://cdn-icons-png.flaticon.com/512/149/149071.png';

                analysisData.push({
                    id: userDoc.id,
                    name: userPublic.name,
                    photoURL: userPhoto,
                    role: userPublic.role,
                    lojaNome: userPublic.loja || 'N/D',
                    enderecoDisplay: `${privData.endereco || ''}, ${privData.numero || ''} - ${privData.bairro || ''}`,
                    geoData: {
                        cep: privData.cep ? privData.cep.replace(/\D/g, '') : null,
                        numero: privData.numero,
                        endereco: privData.endereco,
                        cidade: privData.cidade,
                        estado: privData.estado
                    },
                    userCoords: privData.coords || null,
                    lojaCoords: lojaDestino ? { lat: lojaDestino.lat, lng: lojaDestino.lng } : null,
                    distanciaKM: (privData.coords && lojaDestino) ? calculateDistance(privData.coords.lat, privData.coords.lon, lojaDestino.lat, lojaDestino.lng) : null
                });
            }
            setUsersAnalysis(analysisData);
            if (currentStores.length > 0) {
                setMapCenter([currentStores[0].lat, currentStores[0].lng]);
                setMapZoom(12); 
            }
        } catch (error) { console.error("Erro ao buscar usuários:", error); } 
        finally { setLoading(false); }
    };

    // --- GEOLOCALIZAÇÃO ---
    const getCoordinatesSmart = async (geoData) => {
        let { endereco: street, cidade: city, estado: state, cep: postalcode, numero } = geoData;
        
        // Prioridade 1: CEP
        if (postalcode && postalcode.length === 8) {
            try {
                await sleep(1000); 
                let url = `https://nominatim.openstreetmap.org/search?format=json&postalcode=${postalcode}&country=Brazil&limit=1`;
                let response = await fetch(url, { headers: { 'User-Agent': 'HubGestaoApp/1.0' } });
                let data = await response.json();
                if (data && data.length > 0) return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
            } catch (err) {}
        }
        
        // Prioridade 2: Endereço
        if (!city || !state) return null;
        try {
            await sleep(1000); 
            let queryStr = `street=${encodeURIComponent(numero ? `${numero} ${street}` : street)}&city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}&country=Brazil`;
            let url = `https://nominatim.openstreetmap.org/search?format=json&${queryStr}&limit=1`;
            let response = await fetch(url, { headers: { 'User-Agent': 'HubGestaoApp/1.0' } });
            let data = await response.json();
            if (data && data.length > 0) return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
            return null;
        } catch (error) { return null; }
    };

    // --- AÇÕES DE ATUALIZAÇÃO ---
    const handleRecalculateSingle = async (user, silent = false) => {
        if (!user.geoData.cep && !user.geoData.cidade) {
            if(!silent) alert(`Usuário ${user.name} sem endereço/CEP.`);
            return false;
        }
        if (!silent && !window.confirm(`Buscar coordenadas para: ${user.name}?`)) return false;

        if(!silent) setLoading(true);
        const coords = await getCoordinatesSmart(user.geoData);

        if (coords) {
            await updateDoc(doc(db, 'usersprivado', user.id), { coords: coords });
            setUsersAnalysis(prev => prev.map(u => {
                if (u.id === user.id) {
                    const newU = { ...u, userCoords: coords };
                    if (newU.lojaCoords) {
                        newU.distanciaKM = calculateDistance(coords.lat, coords.lon, newU.lojaCoords.lat, newU.lojaCoords.lng);
                    }
                    return newU;
                }
                return u;
            }));
            if(!silent) alert("Localização atualizada!");
            if(!silent) setLoading(false);
            return true;
        } else {
            if(!silent) alert("Endereço não encontrado.");
            if(!silent) setLoading(false);
            return false;
        }
    };

    const handleBulkUpdateFiltered = async () => {
        const usersToUpdate = filteredUsers; 
        if (usersToUpdate.length === 0) return;
        if(!window.confirm(`Deseja recalcular a localização para os ${usersToUpdate.length} colaboradores listados?`)) return;

        setSyncProgress({ current: 0, total: usersToUpdate.length, isSyncing: true });
        let processed = 0;
        for (const user of usersToUpdate) {
            await handleRecalculateSingle(user, true);
            processed++;
            setSyncProgress({ current: processed, total: usersToUpdate.length, isSyncing: true });
        }
        setSyncProgress({ current: 0, total: 0, isSyncing: false });
        alert("Processo finalizado!");
    };

    // --- LÓGICA DO MODAL ---
    const handleOpenModal = (loja = null) => {
        if (loja) {
            setEditingLojaId(loja.id);
            setNovaLoja({ ...loja, cep: loja.cep || '' });
        } else {
            setEditingLojaId(null);
            setNovaLoja({ nome: '', endereco: '', numero: '', bairro: '', cidade: '', estado: '', cep: '' });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingLojaId(null);
    };

    const handleBlurCep = async (e) => {
        const cep = e.target.value.replace(/\D/g, '');
        if (cep.length !== 8) return;
        e.target.style.cursor = 'wait';
        try {
            const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
            const d = await r.json();
            if (!d.erro) {
                setNovaLoja(prev => ({ ...prev, endereco: d.logradouro, bairro: d.bairro, cidade: d.localidade, estado: d.uf }));
            }
        } catch(e) {} finally { e.target.style.cursor = 'text'; }
    };

    const handleSaveLoja = async (e) => {
        e.preventDefault();
        setLoading(true);
        const geoDataLoja = {
            cep: novaLoja.cep.replace(/\D/g, ''),
            numero: novaLoja.numero,
            endereco: novaLoja.endereco,
            cidade: novaLoja.cidade,
            estado: novaLoja.estado
        };
        const coords = await getCoordinatesSmart(geoDataLoja);

        if (coords) {
            try {
                if (editingLojaId) {
                    await updateDoc(doc(db, 'Empresas', userData.empresa_id, 'lojas', editingLojaId), { 
                        ...novaLoja, lat: coords.lat, lng: coords.lon 
                    });
                    alert("Loja atualizada!");
                } else {
                    await addDoc(collection(db, 'Empresas', userData.empresa_id, 'lojas'), { 
                        ...novaLoja, lat: coords.lat, lng: coords.lon 
                    });
                    alert("Loja criada!");
                }
                handleCloseModal();
                fetchLojas();
            } catch (err) { alert("Erro ao salvar."); }
        } else {
            alert("Endereço da loja não encontrado. Verifique o CEP.");
        }
        setLoading(false);
    };
    
    const handleDeleteLoja = async (id) => {
        if(window.confirm("Apagar loja?")) {
            await deleteDoc(doc(db, 'Empresas', userData.empresa_id, 'lojas', id));
            fetchLojas();
        }
    };

    // --- FILTRAGEM DE DADOS ---
    const filteredUsers = useMemo(() => {
        return usersAnalysis.filter(u => {
            const searchLower = searchTerm.toLowerCase();
            const searchClean = searchTerm.replace(/\D/g, ''); 
            
            const matchesSearch = 
                u.name.toLowerCase().includes(searchLower) || 
                u.lojaNome.toLowerCase().includes(searchLower) ||
                (u.geoData.cep && searchClean.length > 0 && u.geoData.cep.includes(searchClean));
            
            const matchesLoja = filterLoja === '' || u.lojaNome === filterLoja;
            
            const matchesStatus = filterStatus === '' 
                ? true 
                : filterStatus === 'ok' ? !!u.userCoords : !u.userCoords;
            
            const matchesRadius = filterRadius === 0 
                ? true 
                : (u.distanciaKM !== null && u.distanciaKM <= filterRadius);

            return matchesSearch && matchesLoja && matchesStatus && matchesRadius;
        });
    }, [usersAnalysis, searchTerm, filterLoja, filterStatus, filterRadius]);

    const filteredLojas = useMemo(() => {
        return lojas.filter(l => 
            l.nome.toLowerCase().includes(lojaSearchTerm.toLowerCase()) ||
            l.cidade.toLowerCase().includes(lojaSearchTerm.toLowerCase()) ||
            (l.endereco && l.endereco.toLowerCase().includes(lojaSearchTerm.toLowerCase()))
        );
    }, [lojas, lojaSearchTerm]);

    // Métricas
    const totalUsers = filteredUsers.length;
    const avgDistance = totalUsers > 0 ? (filteredUsers.reduce((acc, curr) => acc + (curr.distanciaKM || 0), 0) / totalUsers).toFixed(1) : 0;
    const nearUsers = filteredUsers.filter(u => u.distanciaKM && u.distanciaKM <= 10).length;

    // Paginação Logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const uniqueLojas = [...new Set(usersAnalysis.map(u => u.lojaNome))].sort();
    
    // Coordenadas para o Círculo de Raio
    const selectedStoreCoords = useMemo(() => {
        if(!filterLoja) return null;
        const store = lojas.find(l => l.nome === filterLoja);
        return store ? { lat: store.lat, lng: store.lng } : null;
    }, [filterLoja, lojas]);

    return (
        <div className="mc-container">
            {/* HEADER */}
            <div className="mc-header">
                <div className="mc-title">
                    <h1>MapIA</h1>
                    <p>Gestão Geográfica de Colaboradores</p>
                </div>
                <div className="mc-tab-container">
                    <button className={`mc-tab-button ${activeTab === 'mapa' ? 'active' : ''}`} onClick={() => setActiveTab('mapa')}>
                        <FaMapMarkerAlt /> Mapa
                    </button>
                    <button className={`mc-tab-button ${activeTab === 'lista' ? 'active' : ''}`} onClick={() => setActiveTab('lista')}>
                        <FaListUl /> Lista
                    </button>
                    <button className={`mc-tab-button ${activeTab === 'lojas' ? 'active' : ''}`} onClick={() => setActiveTab('lojas')}>
                        <FaStore /> Lojas
                    </button>
                </div>
            </div>

            {/* DASHBOARD & FILTROS (Exibidos em Mapa e Lista) */}
            {activeTab !== 'lojas' && (
                <div className="mc-fade-in">
                    <div className="mc-stats-container">
                        <div className="mc-stat-card">
                            <div className="mc-stat-label">Colaboradores</div>
                            <div className="mc-stat-value">{totalUsers}</div>
                        </div>
                        <div className="mc-stat-card">
                            <div className="mc-stat-label">Distância Média</div>
                            <div className="mc-stat-value" style={{color:'var(--accent-success)'}}>{avgDistance} km</div>
                        </div>
                        <div className="mc-stat-card">
                            <div className="mc-stat-label">Perto (&lt;10km)</div>
                            <div className="mc-stat-value" style={{color:'var(--accent-warning)'}}>{nearUsers}</div>
                        </div>
                    </div>

                    <div className="mc-filters-bar">
                        <div className="mc-form-group">
                            <label><FaSearch/> Buscar</label>
                            <input className="mc-input" type="text" placeholder="Nome, CEP..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </div>
                        <div className="mc-form-group">
                            <label><FaStore/> Unidade</label>
                            <select className="mc-select" value={filterLoja} onChange={(e) => setFilterLoja(e.target.value)}>
                                <option value="">Todas</option>
                                {uniqueLojas.map(l => <option key={l} value={l}>{l}</option>)}
                            </select>
                        </div>
                        <div className="mc-form-group">
                            <label><FaRoute/> Raio (km)</label>
                            <div className="mc-slider-container">
                                <input type="range" min="0" max="100" step="5" value={filterRadius} onChange={(e) => setFilterRadius(Number(e.target.value))} style={{flex:1}}/>
                                <div className="mc-slider-value">{filterRadius === 0 ? 'All' : filterRadius}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* BARRA DE PROGRESSO */}
            {syncProgress.isSyncing && (
                <div style={{margin: '20px 0', padding: '15px', background: '#e0f2fe', borderRadius: 'var(--radius-sm)', color: '#0369a1', display:'flex', alignItems:'center', gap:'10px'}}>
                    <FaSyncAlt className="spin-icon" /> Sincronizando coordenadas... {syncProgress.current}/{syncProgress.total}
                </div>
            )}

            <div className="mc-fade-in">
                {/* 1. ABA MAPA */}
                {activeTab === 'mapa' && (
                    <div className="mc-map-wrapper">
                        <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: "100%", width: "100%" }}>
                            <ChangeView center={mapCenter} zoom={mapZoom} />
                            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                            
                            {/* Círculo de Raio */}
                            {selectedStoreCoords && filterRadius > 0 && (
                                <Circle 
                                    center={[selectedStoreCoords.lat, selectedStoreCoords.lng]} 
                                    radius={filterRadius * 1000} 
                                    pathOptions={{ color: '#002454', fillColor: '#002454', fillOpacity: 0.1, dashArray: '10, 10' }}
                                />
                            )}
                            
                            {/* Marcadores Lojas */}
                            {lojas.map(l => {
                                if (filterLoja && l.nome !== filterLoja) return null;
                                return <Marker key={l.id} position={[l.lat, l.lng]} icon={createCustomIcon(visualSettings?.logoUrl || 'https://cdn-icons-png.flaticon.com/512/3082/3082383.png', 'marker-loja')}>
                                    <Popup><strong>{l.nome}</strong><br/>{l.endereco}</Popup>
                                </Marker>
                            })}
                            
                            {/* Marcadores Usuários */}
                            {filteredUsers.map(u => {
                                if (!u.userCoords) return null;
                                return <Marker key={u.id} position={[u.userCoords.lat, u.userCoords.lon]} icon={createCustomIcon(u.photoURL, `marker-user ${u.distanciaKM > 20 ? 'far' : ''}`)}>
                                    <Popup><strong>{u.name}</strong><br/>{u.distanciaKM ? u.distanciaKM.toFixed(1) : '?'} km</Popup>
                                </Marker>
                            })}
                        </MapContainer>
                    </div>
                )}

                {/* 2. ABA LISTA */}
                {activeTab === 'lista' && (
                    <div className="mc-card-content">
                         <div style={{padding:'20px', display:'flex', justifyContent:'flex-end'}}>
                            <button onClick={handleBulkUpdateFiltered} className="mc-btn-primary" disabled={syncProgress.isSyncing}>
                                <FaSyncAlt /> Recalcular Lista
                            </button>
                        </div>
                        <div className="mc-table-responsive">
                            <table className="mc-table">
                                <thead><tr><th>Colaborador</th><th>Endereço</th><th>Unidade</th><th>Distância</th><th>Status</th><th>Ação</th></tr></thead>
                                <tbody>
                                    {currentUsers.map((item) => (
                                        <tr key={item.id}>
                                            <td>
                                                <div className="mc-user-info">
                                                    <img src={item.photoURL} alt="" className="mc-user-img" />
                                                    <div className="mc-user-details"><h4>{item.name}</h4><span>{item.role}</span></div>
                                                </div>
                                            </td>
                                            <td style={{fontSize:'0.9rem', maxWidth:'200px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}} title={item.enderecoDisplay}>
                                                {item.enderecoDisplay}
                                            </td>
                                            <td><span className="mc-badge-loja">{item.lojaNome}</span></td>
                                            <td>
                                                {item.distanciaKM !== null 
                                                    ? <span className={`mc-badge-dist ${item.distanciaKM > 20 ? 'danger' : 'success'}`}>{item.distanciaKM.toFixed(1)} km</span> 
                                                    : '--'
                                                }
                                            </td>
                                            <td>{item.userCoords ? <FaCheck color="var(--accent-success)"/> : <FaExclamationTriangle color="var(--accent-warning)"/>}</td>
                                            <td><button onClick={() => handleRecalculateSingle(item)} className="mc-btn-icon"><FaRedo/></button></td>
                                        </tr>
                                    ))}
                                    {currentUsers.length === 0 && <tr><td colSpan="6" style={{textAlign:'center', padding:'30px', color:'#999'}}>Nenhum colaborador encontrado.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                        <Pagination 
                            currentPage={currentPage} totalPages={totalPages} 
                            onPageChange={setCurrentPage} itemsPerPage={itemsPerPage} 
                            onItemsPerPageChange={setItemsPerPage}
                        />
                    </div>
                )}

                {/* 3. ABA LOJAS */}
                {activeTab === 'lojas' && (
                    <div className="mc-card-content">
                        <div className="mc-lojas-header-flex">
                             <div className="mc-lojas-title-group">
                                 <h3>Unidades</h3>
                                 <div className="mc-search-small-wrapper">
                                     <FaSearch className="mc-search-icon"/>
                                     <input type="text" className="mc-input-small" placeholder="Buscar..." value={lojaSearchTerm} onChange={(e) => setLojaSearchTerm(e.target.value)} />
                                 </div>
                             </div>
                             <button onClick={() => handleOpenModal()} className="mc-btn-primary">
                                 <FaPlus /> Nova Unidade
                             </button>
                        </div>
                        <div className="mc-table-responsive">
                            <table className="mc-table">
                                <thead><tr><th>Loja</th><th>Localização</th><th>Ações</th></tr></thead>
                                <tbody>
                                    {filteredLojas.length === 0 ? <tr><td colSpan="3" style={{textAlign:'center', padding:'20px'}}>Nenhuma loja encontrada.</td></tr> : 
                                    filteredLojas.map(loja => (
                                        <tr key={loja.id}>
                                            <td><strong>{loja.nome}</strong></td>
                                            <td>
                                                <div>{loja.endereco}, {loja.numero}</div>
                                                <div style={{fontSize:'0.8rem', color:'var(--text-muted)'}}>
                                                    {loja.bairro ? `${loja.bairro}, ` : ''}{loja.cidade} - {loja.estado}
                                                </div>
                                            </td>
                                            <td>
                                                <button onClick={() => handleOpenModal(loja)} className="mc-btn-icon"><FaPen/></button>
                                                <button onClick={() => handleDeleteLoja(loja.id)} className="mc-btn-icon" style={{color:'var(--accent-danger)'}}><FaTrash/></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* MODAL RESPONSIVO */}
            {isModalOpen && (
                <div className="mc-modal-overlay">
                    <div className="mc-modal-content">
                        <div className="mc-modal-header">
                            <h3 className="mc-modal-title">
                                {editingLojaId ? <><FaPen/> Editar Unidade</> : <><FaStore/> Nova Unidade</>}
                            </h3>
                        </div>
                        <div className="mc-modal-body">
                            <form id="lojaForm" onSubmit={handleSaveLoja}>
                                <div className="mc-grid-row">
                                    <div className="mc-form-group mc-full-width">
                                        <label>Nome da Unidade</label>
                                        <select 
                                            className="mc-select" 
                                            value={novaLoja.nome} 
                                            onChange={e => setNovaLoja({...novaLoja, nome: e.target.value})} 
                                            required 
                                        >
                                            <option value="">Selecione uma unidade...</option>
                                            {uniqueLojas.filter(l => l && l !== 'N/D').map((loja, index) => (
                                                <option key={index} value={loja}>{loja}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="mc-form-group">
                                        <label>CEP</label>
                                        <input className="mc-input" type="text" value={novaLoja.cep} onChange={e => setNovaLoja({...novaLoja, cep: e.target.value})} onBlur={handleBlurCep} maxLength="8" placeholder="00000000"/>
                                    </div>
                                    <div className="mc-form-group">
                                        <label>Cidade / UF</label>
                                        <input className="mc-input" type="text" value={novaLoja.cidade ? `${novaLoja.cidade}/${novaLoja.estado}` : ''} disabled style={{background:'#f9f9f9'}}/>
                                    </div>
                                    <div className="mc-form-group mc-full-width">
                                        <label>Endereço</label>
                                        <input className="mc-input" type="text" value={novaLoja.endereco} onChange={e => setNovaLoja({...novaLoja, endereco: e.target.value})} required />
                                    </div>
                                    <div className="mc-form-group">
                                        <label>Número</label>
                                        <input className="mc-input" type="text" value={novaLoja.numero} onChange={e => setNovaLoja({...novaLoja, numero: e.target.value})} required />
                                    </div>
                                    <div className="mc-form-group">
                                        <label>Bairro</label>
                                        <input className="mc-input" type="text" value={novaLoja.bairro} onChange={e => setNovaLoja({...novaLoja, bairro: e.target.value})} />
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div className="mc-modal-footer">
                            <button type="submit" form="lojaForm" className="mc-btn-primary" disabled={loading}>
                                {loading ? 'Salvando...' : (editingLojaId ? 'Salvar Alterações' : 'Criar Unidade')}
                            </button>
                            <button type="button" className="mc-btn-secondary" onClick={handleCloseModal}>
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MapaCalor;